import React, { useState, useCallback } from 'react';
import { Box, Container, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, PageTitleBar } from '@/components';
import FilterDailyComparison from '@/components/filters/FilterDailyComparison';
import { notificationService } from '@core/notifications/NotificationService';
import { getMenuData } from '@core/data/menuData';
import WcnApexchart from '@/components/charts/WcnApexchart';
import AppCard from '@/components/appcard/AppCard';
import { zoneTrackerService } from '@/services/zoneTrackerService';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

interface ComparisonArgs {
    shopId1: string;
    deviceId1: string;
    moduleId1: string;
    day1Start: string;
    day1End: string;
    shopId2: string;
    deviceId2: string;
    moduleId2: string;
    day2Start: string;
    day2End: string;
    interval: number;
}

const ZoneTrackerDailyComparison: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading1, setLoading1] = useState(false);
    const [loading2, setLoading2] = useState(false);
    const [series1, setSeries1] = useState<any[]>([]);
    const [series2, setSeries2] = useState<any[]>([]);
    const [heading1, setHeading1] = useState(t('customerFlow'));
    const [heading2, setHeading2] = useState(t('customerFlow'));

    const loadChartData = async (deviceId: string, moduleId: string, start: string, end: string, interval: number) => {
        const data = await zoneTrackerService.loadDailyDetails(deviceId, moduleId, start, end, interval);

        const newSeries = [
            { name: 'Max', data: [] as any[] },
            { name: t('average'), data: [] as any[] },
            { name: 'Min', data: [] as any[] }
        ];

        data.forEach((element) => {
            newSeries[0].data.push({ x: element.intervalStart, y: element.max });
            newSeries[1].data.push({ x: element.intervalStart, y: element.avg });
            newSeries[2].data.push({ x: element.intervalStart, y: element.min });
        });

        return newSeries;
    };

    const loadData = useCallback(
        async (args: ComparisonArgs) => {
            setHeading1(`${t('fillLevel')} ${args.moduleId1}`);
            setHeading2(`${t('fillLevel')} ${args.moduleId2}`);

            // Load Chart 1
            setLoading1(true);
            try {
                const data1 = await loadChartData(
                    args.deviceId1,
                    args.moduleId1,
                    args.day1Start,
                    args.day1End,
                    args.interval
                );
                setSeries1(data1);
            } catch (error) {
                console.error('Failed to load chart 1:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading1(false);
            }

            // Load Chart 2
            setLoading2(true);
            try {
                const data2 = await loadChartData(
                    args.deviceId2,
                    args.moduleId2,
                    args.day2Start,
                    args.day2End,
                    args.interval
                );
                setSeries2(data2);
            } catch (error) {
                console.error('Failed to load chart 2:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading2(false);
            }
        },
        [t]
    );

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <PageTitleBar
                menus={getMenuData()}
                currentRoute={{
                    path: '/zonetracker/dailycomparison',
                    meta: {
                        breadcrumb: {
                            breadcrumbInactive: 'Statistics / Zone Tracker',
                            breadcrumbActive: 'Daily Comparison'
                        }
                    }
                }}
                t={t}
                titleSlot={`${t('zoneTracker')} ${t('dailyComparison')}`}
            />
            <Container maxWidth={false} sx={{ py: 3 }}>
                <FilterDailyComparison onLoadData={loadData} forType="zoneTracker" intervals={[60, 30, 15, 10, 5, 1]} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        {loading1 ? (
                            <LoadingSpinner showBackground={false} />
                        ) : (
                            <AppCard
                                heading={heading1}
                                icon="border_all"
                                iconClass=""
                                colClasses="flex col-12"
                                customClasses="mb-0"
                                fullBlock={false}
                                footer={false}
                            >
                                <WcnApexchart
                                    type="line"
                                    chartId="Zone-1"
                                    series={series1}
                                    height={550}
                                    xAxisType="datetime"
                                    syncChartId="ZoneTracker"
                                    xAxisShowTime={true}
                                />
                            </AppCard>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {loading2 ? (
                            <LoadingSpinner showBackground={false} />
                        ) : (
                            <AppCard
                                heading={heading2}
                                icon="border_all"
                                iconClass=""
                                colClasses="flex col-12"
                                customClasses="mb-0"
                                fullBlock={false}
                                footer={false}
                            >
                                <WcnApexchart
                                    type="line"
                                    chartId="Zone-2"
                                    series={series2}
                                    height={550}
                                    xAxisType="datetime"
                                    syncChartId="ZoneTracker"
                                    xAxisShowTime={true}
                                />
                            </AppCard>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

ZoneTrackerDailyComparison.displayName = 'ZoneTrackerDailyComparison';

export default ZoneTrackerDailyComparison;
